import { createUnifiedSkybus } from "@skydio/skybus/src/unified/createUnifiedSkybus";
import SkybusProviderLive from "@skydio/skybus/src/provider/live";
import SkybusLive from "@skydio/skybus/src/live";
import { tunnel_channel_t } from "@skydio/lcm/types/skybus_tunnel/tunnel_channel_t";
import { LivekitTransportArgs, RemoteDeviceType } from "@skydio/skybus/src/transport/types";
import LivekitTransportLayer from "@skydio/skybus/src/transport/livekit/livekit_transport_layer";
import { Connector } from "@skydio/skybus/src/transport/base";
import { broadcast_channels_p } from "@skydio/lcm/types/ptree/broadcast_channels_p";
import { broadcast_channel_t } from "@skydio/lcm/types/iot_api/broadcast_channel_t";

import type {
  OptimisticAnalyticsLogger,
  OptimisticChannelConfig,
} from "@skydio/skybus/src/unified/optimisticUnifiedSkybus";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyOptimisticConfig = OptimisticChannelConfig<any, any>;

/**
 * Per-device optimistic configs and (optionally) a shared analytics logger.
 *
 * The vehicle and dock live on separate tunnels with different sets of
 * channels, so registering a config against the wrong device is a real bug
 * (we'd subscribe the wrong transport and publish requests to the wrong
 * endpoint). Callers pass the vehicle and dock config arrays independently.
 *
 * `analyticsLogger`, if provided, is shared across the vehicle and dock
 * skybus instances. Wire it from the application layer (e.g. customer
 * portal) to `datadogRum.addAction` so we can observe the optimistic
 * lifecycle in production for debugging customer-reported issues.
 */
export interface OptimisticChannelConfigsByDevice {
  vehicle?: ReadonlyArray<AnyOptimisticConfig>;
  dock?: ReadonlyArray<AnyOptimisticConfig>;
  analyticsLogger?: OptimisticAnalyticsLogger;
}

const createSkybusStack = <T extends Connector>(
  connector: T,
  tunnelChannels: tunnel_channel_t[],
  broadcastChannels: broadcast_channel_t[],
  optimisticConfigs?: ReadonlyArray<AnyOptimisticConfig>,
  analyticsLogger?: OptimisticAnalyticsLogger
) => {
  const skybusLive = new SkybusLive(connector, tunnelChannels, broadcastChannels);
  const provider = new SkybusProviderLive(skybusLive);
  // Delegate the "plain vs optimistic" decision to the skybus package so
  // every backend picks it up from the same place (see
  // createUnifiedSkybus.ts for details and tests).
  const skybus = createUnifiedSkybus(provider, optimisticConfigs, analyticsLogger);
  return { connector, skybusLive, skybus };
};

export const setupLivekitSkybus = (
  transportArgs: LivekitTransportArgs,
  tunnelChannels?: tunnel_channel_t[],
  dockTunnelChannels?: tunnel_channel_t[],
  broadcastChannels?: broadcast_channels_p,
  optimisticConfigs?: OptimisticChannelConfigsByDevice
) => {
  const transportLayer = new LivekitTransportLayer(transportArgs);
  const vehicleConnector = transportLayer.createConnector(RemoteDeviceType.VEHICLE);
  const vehicleStack = createSkybusStack(
    vehicleConnector,
    tunnelChannels ?? [],
    broadcastChannels?.vehicle ?? [],
    optimisticConfigs?.vehicle,
    optimisticConfigs?.analyticsLogger
  );
  const dockConnector = transportLayer.createConnector(RemoteDeviceType.DOCK);
  const dockStack = createSkybusStack(
    dockConnector,
    dockTunnelChannels ?? [],
    broadcastChannels?.dock ?? [],
    optimisticConfigs?.dock,
    optimisticConfigs?.analyticsLogger
  );
  return { transportLayer, devices: [vehicleStack, dockStack] };
};
